import { useState } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/stores/auth';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { toast } from '@/components/ui/Toast';
import {
  pushPermissionState,
  registerPushIfPermitted,
  requestPushPermissionAndRegister,
  unregisterPushNotifications,
} from '@/lib/pushNotifications';

export default function NotificationsSettingsPage() {
  const { firebaseUser, userDoc } = useAuth();
  const channels = userDoc?.preferences?.notificationChannels ?? {
    inApp: true,
    push: true,
    email: false,
  };
  const [draft, setDraft] = useState(channels);
  const [saving, setSaving] = useState(false);
  const [enablingPush, setEnablingPush] = useState(false);
  const permission = pushPermissionState();

  if (!firebaseUser) return null;

  const save = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'users', firebaseUser.uid), {
        'preferences.notificationChannels': draft,
        updatedAt: serverTimestamp(),
      });
      if (!draft.push) {
        await unregisterPushNotifications();
      } else if (Notification.permission === 'granted') {
        await registerPushIfPermitted();
      }
      toast.success('Saved.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const enablePush = async () => {
    setEnablingPush(true);
    try {
      const result = await requestPushPermissionAndRegister();
      if (result === 'granted') {
        setDraft((d) => ({ ...d, push: true }));
        toast.success('Push notifications enabled.');
      } else if (result === 'denied') {
        toast.error('Permission denied. Enable notifications in your browser settings.');
      } else {
        toast.error('Push is not supported in this browser.');
      }
    } finally {
      setEnablingPush(false);
    }
  };

  const toggle = (k: keyof typeof draft) => setDraft((d) => ({ ...d, [k]: !d[k] }));

  return (
    <section className="mx-auto max-w-xl px-6 py-6">
      <h1 className="font-display text-3xl">Notifications</h1>
      <Card className="mt-6 space-y-3">
        {(['inApp', 'push', 'email'] as const).map((k) => (
          <label key={k} className="flex items-center justify-between text-sm">
            <span className="capitalize">{k.replace('App', '-app')}</span>
            <input type="checkbox" checked={draft[k]} onChange={() => toggle(k)} />
          </label>
        ))}
      </Card>

      {draft.push && permission !== 'granted' && permission !== 'unsupported' && (
        <Card className="mt-4 space-y-3">
          <p className="text-sm text-makhe-muted">
            Allow browser notifications to get booking updates when Makhe isn&apos;t open.
          </p>
          <Button onClick={enablePush} loading={enablingPush} className="w-full">
            Turn on notifications
          </Button>
        </Card>
      )}

      {permission === 'unsupported' && (
        <p className="mt-3 text-xs text-makhe-muted">
          Push isn&apos;t available in this browser (or this build). On iPhone, install Makhe to your
          Home Screen and open it from there.
        </p>
      )}

      {permission === 'denied' && (
        <p className="mt-3 text-xs text-makhe-muted">
          Notifications are blocked for this site. Unblock them in your browser settings, then tap
          Turn on above.
        </p>
      )}
      <Button className="mt-6 w-full" loading={saving} onClick={save}>
        Save
      </Button>
    </section>
  );
}
